import React, { FC, useCallback, useEffect, useState } from 'react';
import { WebUxPage, WebUxPageContent, WebUxPageItem } from '../webux/webux-layouts/WebUxPage';
import { Button, Modal, Space } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import { QueryBuilder } from '../../components/molecules/QueryBuilder/QueryBuilder';
import { DataTable, DataTablePagination } from '../../components/molecules/DataTable/DataTable';
import { useConnection } from '../../connection/Application';
import { InfoView, InfoViewItem } from '../../components/info-view/InfoView';
import { ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { FlexBox } from '../../components/atoms/FlexBox';
import { FormModal, useFormModal } from '../../components/form-modal/FormModal';
import Form from '../../form/Form';
import { ConfigData } from '../../entity/ConfigData';

const columns = [
  {
    title: 'Key',
    dataIndex: 'key',
    key: 'key',
  },
  {
    title: 'Value',
    dataIndex: 'value',
    width: '60%',
    key: 'value',
  },
];

const schema = [
  {
    id: 'key',
    name: 'Key',
    type: 'string',
  },
  {
    id: 'value',
    name: 'Value',
    type: 'string',
  },
];

const Configuration: FC = () => {
  const [visible, setVisible] = useState<boolean>(false);
  const [filter, setFilter] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState();
  const [total, setTotal] = useState<number>(0);
  const [current, setCurrent] = useState<{ id: number; value: string }>();
  const [pagination, setPagination] = useState<DataTablePagination>({ page: 1, size: 10 });
  const connection = useConnection();
  const createForm = useFormModal<ConfigData>();
  const editForm = useFormModal<ConfigData>();

  const getConfig = useCallback(
    (filter: any, pagination: DataTablePagination) => {
      setLoading(true);
      connection
        .get('config', { ...pagination, ...filter })
        .then((response) => {
          setData(response.config.map((e: any) => ({ ...e, key: e.id })));
          setTotal(response.total);
        })
        .finally(() => setLoading(false));
    },
    [connection],
  );

  useEffect(() => {
    getConfig({}, { page: 1, size: 10 });
  }, [connection, getConfig]);

  const addConfig = useCallback(
    async (Config: ConfigData) => {
      try {
        await connection.post('config', Config);
        createForm.close();
        getConfig(filter, pagination);
      } catch (e: any) {
        if (e?.body?.body?.errors) {
          createForm.setErrors(e.body.body.errors);
        } else {
          createForm.setErrors([]);
        }
      }
    },
    [connection, createForm, filter, getConfig, pagination],
  );

  const editConfig = useCallback(
    async (config: ConfigData) => {
      try {
        const configId = current && current.id;
        config.id = configId;
        await connection.patch(`config/${configId}`, config);
        editForm.close();
        setVisible(false);
        getConfig(filter, pagination);
      } catch (e: any) {
        if (e.body?.body?.errors) {
          editForm.setErrors(e.body.body.errors);
        } else {
          editForm.setErrors([]);
        }
      }
    },
    [connection, current, editForm, filter, getConfig, pagination],
  );

  const search = useCallback(
    (filter: any, pagination: DataTablePagination) => {
      setLoading(true);
      connection
        .get('config', { ...pagination, ...filter })
        .then((response) => {
          setData(response.config);
          setTotal(response.total);
        })
        .finally(() => setLoading(false));
    },
    [connection],
  );

  const deleteConfig = useCallback(() => {
    const configId = current && current.id;
    Modal.confirm({
      title: 'Delete Configuration',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure delete to this Configuration?',
      okText: 'Delete',
      okType: 'danger',
      onOk() {
        connection.delete(`config/${configId}`).then(() => {
          setVisible(false);
          getConfig(filter, pagination);
        });
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  }, [connection, current, filter, getConfig, pagination]);

  return (
    <WebUxPage>
      <PageHeader
        ghost={false}
        style={{ background: '#FFF' }}
        title="Configuration"
        subTitle="Configuration management"
      />
      <WebUxPageContent>
        <WebUxPageItem
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <QueryBuilder
            searchPlaceholder="Search Configuration"
            search={true}
            value={filter}
            onChange={setFilter}
            onSearch={(value) => {
              setPagination({ page: 1, size: 10 });
              search(value, pagination);
            }}
            schema={[]}
          />
          <Space>
            <Button
              onClick={() => {
                setFilter({});
                setPagination({ page: 1, size: 10 });
                search({}, { page: 1, size: 10 });
              }}>
              Reset
            </Button>
            <Button type="primary">Search</Button>
          </Space>
        </WebUxPageItem>
        <WebUxPageItem>
          <Space direction={'vertical'} style={{ width: '100%' }} size={16}>
            <FlexBox horizontalAlign={'right'}>
              <Button
                icon={<PlusOutlined />}
                key="1"
                type="primary"
                onClick={() => createForm.open()}>
                New Configuration
              </Button>
            </FlexBox>
            <DataTable
              columns={columns}
              total={total}
              loading={loading}
              dataSource={data}
              onPaginationChange={(p) => {
                setPagination(p);
                if (pagination.page !== p.page || pagination.size !== p.size) {
                  search(filter, p);
                }
              }}
              onClick={(item: { id: number; value: string }) => {
                setVisible(true);
                setCurrent(item);
              }}
              dtPagination={pagination}
            />
          </Space>
        </WebUxPageItem>
      </WebUxPageContent>
      <Modal
        open={visible}
        title={'Configuration'}
        onCancel={() => {
          setVisible(false);
        }}
        footer={
          <Space align={'end'}>
            <Button
              onClick={() => {
                setVisible(false);
              }}>
              Close
            </Button>
            <Button onClick={() => editForm.open(current)}>Edit</Button>
            <Button type="primary" onClick={deleteConfig}>
              Delete
            </Button>
          </Space>
        }>
        <InfoView>
          <InfoViewItem title={(current && current.value) ?? ''} />
        </InfoView>
      </Modal>
      <FormModal title={'Add Configuration'} onSubmit={addConfig} form={createForm}>
        <Form.Items schema={schema} />
      </FormModal>
      <FormModal title={'Edit Configuration'} onSubmit={editConfig} form={editForm}>
        <Form.Items schema={schema} />
      </FormModal>
    </WebUxPage>
  );
};
export default Configuration;
